// Sanity CMS queries for regions and areas
// These are placeholder queries that will be updated once the new schema is implemented
import { islandScopedDocumentFilter } from './sanity';

export const regionQueries = {
  // Get all regions for listing (matches your actual schema)
  getAllRegions: `*[_type == "region" && ${islandScopedDocumentFilter()}] | order(title) {
    _id,
    title,
    slug,
    key,
    oneLiner,
    shortDescription,
    heroImage {
      asset->{
        url
      },
      alt
    },
    "areaCount": count(*[_type == "area" && ${islandScopedDocumentFilter()} && references(^._id)])
  }`,

  // Get single region by slug with all details  
  getRegionBySlug: `*[_type == "region" && ${islandScopedDocumentFilter()} && slug.current == $slug][0] {
    _id,
    title,
    slug,
    key,
    oneLiner,
    shortDescription,
    bestFor,
    tradeoffs,
    logisticsNotes,
    heroImage {
      asset->{
        url
      },
      alt
    },
    seo {
      title,
      description
    },
    reviewArticle,
    signatureExperiences[]->{
      _id,
      name,
      "slug": slug.current,
      "area": coalesce(areaRef->title, area),
      category,
      inventoryClass,
      activityType,
      experienceTags,
      cost,
      shortDescription,
      images[]{
        asset->{ _id, url }
      }
    },
    "areas": areas[]->{
      _id,
      title,
      slug,
      oneLiner,
      heroImage {
        asset->{
          url
        },
        alt
      }
    }
  }`,

  // Get regions with their areas for navigation
  getRegionsWithAreas: `*[_type == "region" && ${islandScopedDocumentFilter()}] | order(title) {
    _id,
    title,
    slug,
    key,
    "areas": areas[]->{
      _id,
      title,
      slug
    }
  }`
};

export const areaQueries = {
  // Get all areas for listing (matches your actual schema)
  getAllAreas: `*[_type == "area" && ${islandScopedDocumentFilter()}] | order(title) {
    _id,
    title,
    slug,
    oneLiner,
    fit,
    tradeoffs,
    walkability,
    beachProfile,
    diningDensity,
    heroImage {
      asset->{
        url
      },
      alt
    },
    "region": region->{
      _id,
      title,
      slug,
      key
    }
  }`,

  // Get single area by slug with all details
  getAreaBySlug: `*[_type == "area" && ${islandScopedDocumentFilter()} && slug.current == $slug][0] {
    _id,
    title,
    slug,
    oneLiner,
    fit,
    tradeoffs,
    walkability,
    beachProfile,
    diningDensity,
    logisticsNotes,
    heroImage {
      asset->{
        url
      },
      alt
    },
    seo {
      title,
      description
    },
    reviewArticle,
    signatureExperiences[]->{
      _id,
      name,
      "slug": slug.current,
      "area": coalesce(areaRef->title, area),
      category,
      inventoryClass,
      activityType,
      experienceTags,
      cost,
      shortDescription,
      images[]{
        asset->{ _id, url }
      }
    },
    "region": region->{
      _id,
      title,
      slug,
      key
    }
  }`,

  // Get areas by region
  getAreasByRegion: `*[_type == "area" && ${islandScopedDocumentFilter()} && region->slug.current == $regionSlug] | order(title) {
    _id,
    title,
    slug,
    oneLiner,
    heroImage {
      asset->{
        url
      },
      alt
    }
  }`
};

export const contentQueries = {
  // Get activities by region
  getActivitiesByRegion: `*[
    _type == "activity" &&
    ${islandScopedDocumentFilter()} &&
    coalesce(operationalStatus, "active") == "active" &&
    areaRef->region->slug.current == $regionSlug
  ] | order(name) [0...$limit] {
    _id,
    name,
    "slug": slug.current,
    shortDescription,
    "area": coalesce(areaRef->title, area),
    category,
    inventoryClass,
    activityType,
    experienceTags,
    images[0...1]{
      asset->{url},
      alt
    },
    cost
  }`,

  // Get activities by area
  getActivitiesByArea: `*[
    _type == "activity" &&
    ${islandScopedDocumentFilter()} &&
    coalesce(operationalStatus, "active") == "active" &&
    areaRef->slug.current == $areaSlug
  ] | order(name) [0...$limit] {
    _id,
    name,
    "slug": slug.current,
    shortDescription,
    "area": coalesce(areaRef->title, area),
    category,
    inventoryClass,
    activityType,
    experienceTags,
    images[0...1]{
      asset->{url},
      alt
    },
    cost
  }`,

  // Get restaurants by region
  getRestaurantsByRegion: `*[
    _type == "restaurant" &&
    ${islandScopedDocumentFilter()} &&
    coalesce(operationalStatus, "active") == "active" &&
    areaRef->region->slug.current == $regionSlug
  ] | order(name) [0...$limit] {
    _id,
    name,
    slug,
    shortDescription,
    "area": coalesce(areaRef->title, area),
    "region": areaRef->region->title,
    images[0...1] {
      asset->{
        url
      },
      alt
    },
    cuisine,
    priceRange,
    serviceType
  }`,

  // Get restaurants by area
  getRestaurantsByArea: `*[
    _type == "restaurant" &&
    ${islandScopedDocumentFilter()} &&
    coalesce(operationalStatus, "active") == "active" &&
    areaRef->slug.current == $areaSlug
  ] | order(name) [0...$limit] {
    _id,
    name,
    slug,
    shortDescription,
    "area": coalesce(areaRef->title, area),
    "region": areaRef->region->title,
    images[0...1] {
      asset->{
        url
      },
      alt
    },
    cuisine,
    priceRange,
    serviceType
  }`,

  // Get lodging by region
  getLodgingByRegion: `*[
    _type == "lodging" &&
    ${islandScopedDocumentFilter()} &&
    coalesce(operationalStatus, "active") == "active" &&
    areaRef->region->slug.current == $regionSlug
  ] | order(name) [0...$limit] {
    _id,
    name,
    "slug": slug.current,
    shortDescription,
    images[0...1] {
      asset->{
        url
      },
      alt
    },
    lodgingType,
    priceRange
  }`,

  // Get lodging by area
  getLodgingByArea: `*[
    _type == "lodging" &&
    ${islandScopedDocumentFilter()} &&
    coalesce(operationalStatus, "active") == "active" &&
    areaRef->slug.current == $areaSlug
  ] | order(name) [0...$limit] {
    _id,
    name,
    "slug": slug.current,
    shortDescription,
    images[0...1] {
      asset->{
        url
      },
      alt
    },
    lodgingType,
    priceRange
  }`,

  // Get blog posts related to region
  getBlogPostsByRegion: `*[_type == "blogPost" && ${islandScopedDocumentFilter()} && $regionSlug in regions[]->slug.current] | order(publishedAt desc) [0...$limit] {
    _id,
    title,
    slug,
    excerpt,
    publishedAt,
    mainImage {
      asset->{
        url
      },
      alt
    },
    "category": category->title
  }`,

  // Get blog posts related to area
  getBlogPostsByArea: `*[_type == "blogPost" && ${islandScopedDocumentFilter()} && $areaSlug in areas[]->slug.current] | order(publishedAt desc) [0...$limit] {
    _id,
    title,
    slug,
    excerpt,
    publishedAt,
    mainImage {
      asset->{
        url
      },
      alt
    },
    "category": category->title
  }`
};

export const homebaseResultQueries = {
  getSupportContent: `{
    "guides": *[
      _type == "guidePage" &&
      ${islandScopedDocumentFilter()} &&
      defined(slug.current) &&
      (
        count(coalesce(topPicks, [])[ref->areaRef->slug.current == $areaSlug || ref->areaRef->region->slug.current == $regionSlug]) > 0 ||
        count(coalesce(items, [])[ref->areaRef->slug.current == $areaSlug || ref->areaRef->region->slug.current == $regionSlug]) > 0
      )
    ] | order(hubSpoke desc, title asc) [0...3] {
      _id,
      title,
      "slug": slug.current,
      pageType,
      hubSpoke
    },
    "blogPosts": *[
      _type == "blogPost" &&
      ${islandScopedDocumentFilter()} &&
      defined(slug.current) &&
      count(coalesce(furtherReading, [])[
        areaRef->slug.current == $areaSlug ||
        regionRef->slug.current == $regionSlug
      ]) > 0
    ] | order(publishedAt desc) [0...2] {
      _id,
      title,
      "slug": slug.current,
      excerpt,
      "category": category->title
    }
  }`,
};

export const aboutQueries = {
  getActiveEmployees: `*[_type == "employee" && active == true] | order(sortOrder asc, _createdAt asc) {
    _id,
    name,
    roleTitle,
    roleShortDescription,
    shortBio,
    bio,
    topKauaiMoment,
    externalEmail,
    isVirtual,
    headshot{
      asset,
      alt,
      crop,
      hotspot
    }
  }`,
};

export const guideQueries = {
  getGuideBySlug: `*[_type == "guidePage" && ${islandScopedDocumentFilter()} && slug.current == $slug][0] {
    _id,
    title,
    "slug": slug.current,
    primaryKeyword,
    pageType,
    hubSpoke,
    heroImage {
      asset->{ url },
      alt
    },
    intro,
    // Parent hub for spoke pages
    hubPage->{
      _id,
      title,
      "slug": slug.current
    },
    // Top picks for hub pages
    topPicks[]{
      headline,
      "ref": ref->{
        _id,
        _type,
        "slug": slug.current,
        name,
        title,
        area,
        shortDescription,
        mainImage { asset->{ url }, alt },
        images[]{ asset->{ url }, alt },
        heroImage { asset->{ url }, alt }
      }
    },
    // Items for spoke pages (flat list)
    items[]{
      headline,
      "ref": ref->{
        _id,
        _type,
        "slug": slug.current,
        name,
        title,
        area,
        shortDescription,
        mainImage { asset->{ url }, alt },
        images[]{ asset->{ url }, alt },
        heroImage { asset->{ url }, alt }
      }
    },
    // Spoke pages that reference this hub
    "spokePages": *[_type == "guidePage" && ${islandScopedDocumentFilter()} && hubPage._ref == ^._id] | order(title) {
      _id,
      title,
      "slug": slug.current,
      heroImage {
        asset->{ url },
        alt
      },
      // Preview: first 4 items
      "previewItems": items[0...4]{
        "ref": ref->{
          _id,
          _type,
          name,
          title,
          mainImage { asset->{ url }, alt },
          images[]{ asset->{ url }, alt },
          heroImage { asset->{ url }, alt }
        }
      }
    },
    // Sibling spokes (same hub, excluding self)
    "siblingSpokes": *[_type == "guidePage" && ${islandScopedDocumentFilter()} && hubPage._ref == ^.hubPage._ref && _id != ^._id] | order(title) {
      _id,
      title,
      "slug": slug.current
    },
	    relatedGuides[]->{
	      _id,
	      title,
	      "slug": slug.current,
	      pageType,
	      hubSpoke,
	      // Preview: first 4 items for thumbnail grid (hub: topPicks, spoke: items)
	      "previewItems": select(
	        hubSpoke == "hub" => coalesce(topPicks[0...4]{
	          "ref": ref->{
	            _id,
	            _type,
	            name,
	            title,
	            mainImage { asset->{ url }, alt },
	            images[]{ asset->{ url }, alt },
	            heroImage { asset->{ url }, alt }
	          }
	        }, []),
	        coalesce(items[0...4]{
	          "ref": ref->{
	            _id,
	            _type,
	            name,
	            title,
	            mainImage { asset->{ url }, alt },
	            images[]{ asset->{ url }, alt },
	            heroImage { asset->{ url }, alt }
	          }
	        }, [])
	      )
	    },
	    seo{
	      title,
	      description,
      noindex
    },
    lastReviewed
  }`,

  getAllGuides: `*[_type == "guidePage" && ${islandScopedDocumentFilter()} && defined(slug.current)] | order(pageType asc, hubSpoke desc, title asc) {
    _id,
    title,
    "slug": slug.current,
    pageType,
    hubSpoke,
    heroImage{
      asset,
      alt
    },
    hubPage->{
      _id,
      title,
      "slug": slug.current
    }
  }`
};

export const guideIndexQueries = {
  getRegionsForIndex: `*[_type == "region" && ${islandScopedDocumentFilter()} && defined(slug.current)] | order(title) {
    _id,
    title,
    "slug": slug.current,
    heroImage{
      asset,
      alt
    }
  }`,

  getAreasForIndex: `*[_type == "area" && ${islandScopedDocumentFilter()} && defined(slug.current)] | order(title) {
    _id,
    title,
    "slug": slug.current,
    heroImage{
      asset,
      alt
    },
    "region": region->{
      title,
      "slug": slug.current
    }
  }`
};
